import { Button } from "@/components/ui/button";

import { Badge } from "@/components/ui/badge";
import { DocxRenderComponent, TextRenderComponent } from "@/components/shared";
import { Select, SelectGroup, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Document, DocumentFormat } from "@/graphql/__generated__/graphql";
import { useState, useEffect } from "react";
import { DocumentPage } from "@/pages/EditTender/DocumentChat/Components";
import { useTranslation } from "react-i18next";
import { BadgeColourMap } from "@/constants/colours";
import { cn } from "@/lib/utils";

function DocumentsComponent({ documents }: { documents: Document[] }) {
  const { t } = useTranslation(["components", "common"]);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);

  // Set initial selected document when documents are available
  useEffect(() => {
    if (documents.length > 0 && !selectedDocument) {
      setSelectedDocument(documents[0]);
    }
  }, [documents]);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Select
          defaultValue={documents.length > 0 ? documents[0].id.toString() : undefined}
          onValueChange={(value) => {
            const document = documents.find(doc => doc.id.toString() === value);
            if (document) {
              setSelectedDocument(document);
            }
          }}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t("createtendersidebar.documents.placeholder")} />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {
                documents.map((document) => {
                  const badgeClass = BadgeColourMap[document.type] || "bg-gray-200";
                  return (
                    <SelectItem key={document.id} value={document.id.toString()}>
                      <div className="flex gap-4 items-center">
                        <div>{document.fileName}</div>
                        <Badge className={cn("text-xs bg-red-200 text-slate-900", badgeClass)}>
                          {t("common:documenttype." + document.type.toLowerCase())}
                        </Badge>
                      </div>
                    </SelectItem>
                  );
                })
              }
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      <div>
        {selectedDocument?.format === DocumentFormat.Pdf ? (
          <DocumentPage
            pdfDocument={selectedDocument}
            heightClassName="h-[calc(100vh-15rem)]"
            basePdfRender={true}
          />
        ) : selectedDocument?.format === DocumentFormat.Txt ? (
          <TextRenderComponent
            selectedDocument={selectedDocument}
            customHeightClassName="h-[calc(100vh-15rem)]"
          />
        ) : selectedDocument?.format === DocumentFormat.Docx ? (
          <DocxRenderComponent
            selectedDocument={selectedDocument}
            customHeightClassName="h-[calc(100vh-15rem)]"
          />
        ) : selectedDocument && (
          <div className="p-4 flex flex-col gap-4 h-full">
            <p>{selectedDocument.fileName}</p>
            <p className="text-sm text-gray-400">
              {t("edittender.documents.unsupportedfiletype", { format: selectedDocument.format })}
            </p>
            <Button variant="outline" onClick={() => {
              window.open(selectedDocument.url, "_blank");
            }}>
              {t("common:buttons.download")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default DocumentsComponent;
